import isEqual from 'lodash-es/isEqual';
import {
  isRef,
  ref,
  watch,
} from 'vue';

import type { QuerySafeValues } from './RouterUpdater';
import type {
  Ref,
  UnwrapRef,
  WritableComputedRef,
} from 'vue';

/**
 * Человеко-читаемый вид выбранных фильтров. Одна такая штучка представляет только одно значение.
 *
 * В первую очередь нужно для "чипсов" над таблицей.
 *
 * `dropFunction` - колбэк, вызов которого должен удалять выбор этого конкретного значения
 */
export interface HumanReadableSelection {
  valueText: string;
  dropFunction(): void;
}

/**
 * Вернёт либо первый элемент, если аргумент - это массив, либо сам аргумент
 */
export function getFirstIfArray<T> (value: T | Array<T>): T | undefined {
  if (Array.isArray(value)) {
    return value[0];
  } else {
    return value;
  }
}

/**
 * Если аргумент - не массив, то обернёт в массив и вернёт, иначе вернёт аргумент без изменений
 */
export function getWrappedToArray<T> (value: T | Array<T>): Array<T> {
  if (Array.isArray(value)) {
    return value;
  } else {
    return [value];
  }
}
type RWRef<T> = Ref<T> | WritableComputedRef<T>;
export type ValueOrRef<T> = T | RWRef<T>;
type TValueBase = ValueOrRef<unknown>;

export interface BaseFilterConstructorArgs<
  TValue extends TValueBase,
  TName extends string,
  TSpecs extends unknown | RWRef<unknown> = never,
> {
  /**
   * Ключ для удобного обращения. Должен быть уникален в одной системе.
   */
  name: TName;
  /**
   * Значение фильтра
   */
  value: TValue;
  /**
   * Список или какая угодно структура для допустимых значений. Можно передать `ref`/`computed`
   */
  specs?: TSpecs;
  /**
   * Человеко-читаемое название фильтра
   */
  title: string;
  /**
   * Пустое значение, равенство которому будет означать отсутствие выбора
   */
  emptyValue: UnwrapRef<TValue>;
}

export abstract class Filter<
  TValue extends TValueBase,
  TName extends string,
  TSpecs = never,
> {
  /**
   * Ключ для удобного обращения. Должен быть уникален в одной системе.
   */
  readonly name;
  /**
   * Значение фильтра. Это всегда `ref`
   */
  readonly value;
  /**
   * Список или какая угодно структура для допустимых значений
   */
  specs: RWRef<TSpecs>;
  /**
   * stage-значение - нужно для сценария с кнопкой "Применить".
   *
   * Позволяет отделить реальное и визуальное изменение значений фильтров
   */
  readonly stageValue;
  private stageWatcher;
  /**
   * Человеко-читаемое название фильтра
   */
  readonly title;
  /**
   * Пустое значение, равенство которому будет означать отсутствие выбора
   */
  readonly emptyValue: TValue;

  /** Игнорировать синхронизацию с query */
  public synchronizeWithQuery = true;

  constructor (
    args: BaseFilterConstructorArgs<
      TValue | Ref<TValue>,
      TName,
      ValueOrRef<TSpecs>
    >,
  ) {
    // TODO не критично, но можно приделать проверку уникальности имён (можно чисто на типах, можно в рантайме)
    this.name = args.name;
    this.title = args.title;
    this.value = isRef<TValue>(args.value) ? args.value : ref(args.value);
    this.stageValue = ref(structuredClone(this.value.value) as TValue);
    this.specs = isRef<TSpecs>(args.specs) ? args.specs : ref(args.specs) as RWRef<TSpecs>;
    this.emptyValue = args.emptyValue as TValue;
    this.stageWatcher = watch(
      () => this.value.value,
      (newValue) => {
        this.dropStage();
      },
      { deep: true },
    );
  }

  /**
   * Вернёт значение, которое будет сохранено в query
   */
  getQueryValue (): QuerySafeValues | undefined {
    const value = this.value.value;

    if (value == null) {
      return undefined;
    }

    if (Array.isArray(value)) {
      return value.map((x) => x.toString());
    }

    return value.toString();
  }

  /**
   * Метод, которым будет предобрабатываться при считывании значение фильтра из query
   * @param query {@link QuerySafeValues}
   */
  abstract parseQueryValue (query: QuerySafeValues): TValue | null;

  /**
   * Должен или возвращать {@link HumanReadableSelection|человеко-читаемый} вид выбранных значений, или `undefined`
   * @returns `HumanReadableSelection | undefined`
   */
  abstract getSelectionHumanReadable (): Array<HumanReadableSelection> | undefined;

  abstract getSelectedCount (): number | undefined;

  /**
   * Аналогично простому присваиванию значения
   */
  setValue (value: TValue): void {
    this.value.value = value as UnwrapRef<TValue>;
  }

  /**
   * Аналогично `setValue`, но используется для "восстановления" из query (в основном, для кнопок вперёд-назад в браузере)
   *
   * При инициирующем чтении будет всё равно использован {@link setValue}
   */
  queryUpdateValue (value: TValue): void {
    this.setValue(value);
  }

  getValue (): TValue {
    return this.value.value as TValue;
  }

  protected getEmptyValueClone () {
    return structuredClone(this.emptyValue) as UnwrapRef<TValue> | TValue;
  }

  protected getCurrentValueClone () {
    return structuredClone(this.value.value);
  }

  /**
   * Если `toEmpty` установлен в `true`, то сбросит `stage` в `empty` состояние.
   * Иначе синхронизирует реальное значение и stage-значение (сбрасывает stage в реальное)
   */
  dropStage (toEmpty = false): void {
    this.setStageValue(toEmpty ? this.getEmptyValueClone() : this.getCurrentValueClone());
  }

  /**
   * Аналогично простому присваиванию stage-значения
   */
  setStageValue (value: TValue | UnwrapRef<TValue>): void {
    this.stageValue.value = value as UnwrapRef<TValue>;
  }

  /**
   * Принимает stage-значение - синхронизирует реальное значение и stage-значение (присваивает stage в реальное)
   */
  acceptStage (): void {
    this.value.value = this.stageValue.value;
  }

  /**
   * Сбрасывает реальное значение в пустое (empty)
   */
  dropValue (): void {
    this.value.value = this.getEmptyValueClone();
  }

  /**
   * Проверяет, является ли выбор пустым (empty)
   */
  isEmpty (): boolean {
    return isEqual(this.value.value, this.emptyValue);
  }

  dispose (): void {
    this.stageWatcher();
  }

  /**
   * Корректирует выбранное значение до валидного.
   *
   * К примеру, если у нас в спеках доступны для выбора id 1 и 2, то при попадании (из query, например) в value значения 3 - метод должен очистить value
   */
  transformValueToValid () {
    /* Дефолтная реализация пуста */
  }
}
